import React, { useCallback, useEffect, useRef } from 'react';

interface Props {
  /** Called when the user dismisses the dialog (Escape, backdrop click, close button). */
  onClose: () => void;
  /** Accessible name for the dialog, announced on open. */
  title: string;
  /** Extra class on the dialog panel, e.g. "modal-md". */
  panelClassName?: string;
  children: React.ReactNode;
}

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Accessible wrapper for the app's modals.
 *
 * The modals were plain `<div>`s: no `role="dialog"`, no `aria-modal`, no accessible
 * name, no focus trap, no focus restore, no Escape handling and no background scroll
 * lock. A screen-reader user was given no indication a dialog had opened, and a keyboard
 * user could tab straight out of it into the page behind.
 *
 * Renders the same `.modal-overlay` / `.modal` markup the existing stylesheet expects, so
 * it drops in without visual change.
 */
export default function ModalShell({ onClose, title, panelClassName = '', children }: Props) {
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  /*
   * Captured during render, deliberately, not in an effect.
   *
   * Children with `autoFocus` are focused by React during the commit phase, which runs
   * BEFORE this component's effects. Reading document.activeElement from an effect
   * therefore recorded the modal's own autofocused input as the "previously focused"
   * element; that node is detached on close, so the restore silently did nothing.
   * Render runs before the children mount, so this sees the real trigger.
   */
  if (restoreFocusRef.current === null && typeof document !== 'undefined') {
    const active = document.activeElement as HTMLElement | null;
    // Never record something inside the dialog itself.
    if (active && active !== document.body && !panelRef.current?.contains(active)) {
      restoreFocusRef.current = active;
    }
  }

  const focusable = useCallback((): HTMLElement[] => {
    const panel = panelRef.current;
    if (!panel) return [];

    return Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => {
      if (el.hasAttribute('hidden') || el.getAttribute('aria-hidden') === 'true') return false;
      // Deliberately not using offsetParent/offsetHeight: those depend on layout, which
      // makes the trap silently do nothing in any environment without it (jsdom being
      // the obvious one). Computed display/visibility is layout-independent.
      const style = window.getComputedStyle(el);
      return style.display !== 'none' && style.visibility !== 'hidden';
    });
  }, []);

  // Remember what had focus, move focus into the dialog, and restore it on close.
  useEffect(() => {
    const first = focusable()[0];
    if (first) {
      first.focus();
    } else {
      panelRef.current?.focus();
    }

    return () => {
      const target = restoreFocusRef.current;
      if (!target) return;

      // Deferred to the next frame on purpose. Closing a modal usually triggers a
      // refetch-and-re-render in the parent, which lands after this cleanup and drops
      // focus to <body> — so restoring synchronously here got silently undone.
      requestAnimationFrame(() => {
        if (document.contains(target)) target.focus();
      });
    };
  }, [focusable]);

  // Prevent the page behind from scrolling while the dialog is open.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  // Escape to dismiss, and keep Tab cycling inside the dialog.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }

      if (event.key !== 'Tab') return;

      const items = focusable();
      if (items.length === 0) {
        event.preventDefault();
        return;
      }

      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement as HTMLElement | null;

      if (event.shiftKey && (active === first || !panelRef.current?.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [onClose, focusable]);

  return (
    <div
      className="modal-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        ref={panelRef}
        className={`modal ${panelClassName}`.trim()}
        role="dialog"
        aria-modal="true"
        // aria-label rather than aria-labelledby: the callers already render a visible
        // <h2 className="modal-title">, and adding a hidden copy would duplicate the name.
        aria-label={title}
        tabIndex={-1}
      >
        {children}
      </div>
    </div>
  );
}
